import { useGrowthBook } from '@growthbook/growthbook-react';
import { useSelector } from 'react-redux';

import { GB_FEATURES } from 'dpl/shared/constants/gb_features';
import useResourceQuery from 'dpl/shared/fetching/hooks/useResourceQuery';

import { searchAlertsBannerResourceDefinition } from '../utils/queryDefinitions';
import useSearchLocationParams from './useSearchLocationParams';

export interface SearchAlertsMapBanner {
  /** The map banner replaces today's alerts banner content. */
  isShown: boolean;
  isLoading: boolean;
  /** Breeders of the breed within 250 miles of the search. */
  breederCount: number | null;
  /** Center of the search, for the map. */
  latitude: number | null;
  longitude: number | null;
  /** "Brooklyn, NY" for a zip search; null when the search already has a name. */
  locationName: string | null;
}

/**
 * exp_search_alerts_banner (CORE-6678): A/B test of the map alerts banner
 * against today's alerts banner. Mobile web breeders tab only (P-CORE-489).
 *
 * Pass `isEligible` false where the banner doesn't render, so a buyer is
 * only logged as exposed when they'd see one or the other.
 */
export default function useSearchAlertsMapBanner(
  isEligible: boolean
): SearchAlertsMapBanner {
  const { breedSlug } = useSelector(({ server }: any) => server);
  const locationParams = useSearchLocationParams();
  const growthbook = useGrowthBook();

  const isVariant =
    isEligible &&
    growthbook?.getFeatureValue(
      GB_FEATURES.EXP_SEARCH_ALERTS_BANNER,
      'control'
    ) === 'variant';

  const {
    isLoading,
    value: {
      data: {
        breeder_count: breederCount,
        latitude,
        longitude,
        location_name: locationName
      }
    }
  } = useResourceQuery({
    ...searchAlertsBannerResourceDefinition(breedSlug, locationParams),
    enabled: isVariant
  });

  // A location the server can't place has no map to draw; today's banner
  // shows instead.
  const hasRegion = latitude !== null && longitude !== null;

  return {
    isShown: isVariant && (isLoading || hasRegion),
    isLoading,
    breederCount,
    latitude,
    longitude,
    locationName
  };
}
